import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import tributeServices from "../../services/tributeServices";
import ImageWithFallback from "../../components/ImageWithFallback";
import "../../components/RichTextEditor/richtext.css";

const StyledCard = styled(Card)({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
});

const StyledCardMedia = styled(CardMedia)({
  height: 260,
  objectFit: "cover",
});

const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const TributesList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tributeServices
      .get()
      .then((res) => setData(res?.data?.data || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  const sorted = [...data].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));
  const latest = sorted[0] || null;
  const older = sorted.slice(1);

  if (loading) {
    return (
      <Box sx={{ pt: 3, pb: 6 }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 2 } }}>
          <h1 style={{ fontSize: "2.5rem", fontWeight: 700, color: "rgba(26,128,200)", textAlign: "center", marginBottom: "0.5rem", marginTop: "2rem" }}>
            Tributes
          </h1>
          <p style={{ textAlign: "center", marginBottom: "2.5rem", marginTop: 0, color: "#666", fontSize: "1rem" }}>
            In loving memory of our community members
          </p>
          <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2, mb: 3 }} />
          <Grid container spacing={3}>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 1 }} />
                <Skeleton height={24} sx={{ mt: 1 }} />
                <Skeleton height={20} width="80%" />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3, pb: 6, minHeight: "60vh" }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 2 } }}>
        <h1 style={{ fontSize: "2.5rem", fontWeight: 700, color: "rgba(26,128,200)", textAlign: "center", marginBottom: "0.5rem", marginTop: "2rem" }}>
          Tributes
        </h1>
        <p style={{ textAlign: "center", marginBottom: "2.5rem", marginTop: 0, color: "#666", fontSize: "1rem" }}>
          In loving memory of our community members
        </p>

        {sorted.length === 0 ? (
          <Typography align="center" sx={{ py: 4, color: "#666" }}>
            No tributes yet.
          </Typography>
        ) : (
          <>
            {latest && (
              <Card
                component={Link}
                to={`/tributes/${latest._id}`}
                sx={{
                  textDecoration: "none",
                  display: "block",
                  mb: 4,
                  borderRadius: 2,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 30px rgba(0,0,0,0.12)" },
                }}
              >
                <CardActionArea>
                  <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "stretch" }}>
                    <Box sx={{ width: { xs: "100%", md: 400 }, minHeight: 280, flexShrink: 0 }}>
                      <ImageWithFallback
                        path={latest.image}
                        alt={latest.name || "Tribute"}
                        style={{ width: "100%", height: "100%", minHeight: 280, objectFit: "cover", display: "block" }}
                      />
                    </Box>
                    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", p: 3 }}>
                      {latest.shortStatement && (
                        <Typography variant="subtitle1" color="#666" sx={{ mb: 1.5 }}>
                          {latest.shortStatement}
                        </Typography>
                      )}
                      <Typography variant="body1" color="#333" sx={{ lineHeight: 1.7 }}>
                        {stripHtml(latest.description).slice(0, 320)}
                        {stripHtml(latest.description).length > 320 ? "..." : ""}
                      </Typography>
                      <Typography variant="body2" color="rgba(26,128,200)" sx={{ mt: 2, fontWeight: 600 }}>
                        Read full tribute →
                      </Typography>
                    </CardContent>
                  </Box>
                </CardActionArea>
              </Card>
            )}

            {older.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
                  Tribute archive
                </Typography>
                <Grid container spacing={3}>
                  {older.map((item) => (
                    <Grid item xs={12} sm={6} md={4} key={item._id}>
                      <StyledCard>
                        <CardActionArea component={Link} to={`/tributes/${item._id}`}>
                          <StyledCardMedia>
                            <ImageWithFallback
                              path={item.image}
                              alt={item.name || "Tribute"}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          </StyledCardMedia>
                          <CardContent sx={{ flexGrow: 1, textAlign: "left" }}>
                            {item.shortStatement && (
                              <Typography variant="subtitle2" color="#666" sx={{ mb: 0.5 }}>
                                {item.shortStatement}
                              </Typography>
                            )}
                            <Typography variant="body2" color="#333" sx={{ lineHeight: 1.5 }}>
                              {stripHtml(item.description).slice(0, 100)}
                              {stripHtml(item.description).length > 100 ? "..." : ""}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: "rgba(26,128,200)", fontWeight: 600 }}>
                              View tribute →
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default TributesList;
